import React, { useMemo } from 'react';

import type { AvatarProps } from '@atlaskit/avatar-group/types';
import AvatarGroup from '@atlaskit/avatar-group/avatar-group';
import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';

import { renderMoreIndicator } from '../CorePresenceMoreIndicator.tsx';

import { AgentPresenceAvatar } from './AgentPresenceAvatar.tsx';
import { getAgentPresenceKey, type AgentWithPresence } from './AgentPresenceTypes.ts';

const MAX_GROUP_SIZE = 4;

const styles = cssMap({
	root: {
		marginInline: token('space.negative.025'),
	},
	presenceAgentWrapper: {
		display: 'inline-flex',
		flexShrink: 0,
		alignItems: 'center',
		minHeight: '28px',
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
	},
});

export const renderAgent = (agents: AgentWithPresence[], count: number) => {
	return (
		_: any,
		props: AvatarProps & { agentPresenceKey?: string },
		index: number,
	): React.JSX.Element | null => {
		const agent = agents.find(
			(a) =>
				getAgentPresenceKey({
					...a,
					includeActingUser: fg('platform_move_presence_agents'),
				}) === props.agentPresenceKey,
		);

		if (!agent) {
			return null;
		}

		if (fg('platform_move_presence_agents')) {
			return (
				<Box xcss={styles.presenceAgentWrapper}>
					<AgentPresenceAvatar
						agent={agent}
						isOverlapped={index > 0}
						size="small"
						stackIndex={count - index}
						showBorder
					/>
				</Box>
			);
		}

		return (
			<Box xcss={styles.root}>
				<AgentPresenceAvatar agent={agent} stackIndex={count - index} showBorder />
			</Box>
		);
	};
};

export type AgentPresenceAvatarGroupProps = {
	agents: AgentWithPresence[];
	isOpen: boolean;
	onMoreClick: () => void;
};

export const AgentPresenceAvatarGroup = ({
	agents,
	isOpen,
	onMoreClick,
}: AgentPresenceAvatarGroupProps): React.JSX.Element => {
	const data: (AvatarProps & { agentPresenceKey: string })[] = useMemo(() => {
		return agents.map((agent) => ({
			agentPresenceKey: getAgentPresenceKey({
				...agent,
				includeActingUser: fg('platform_move_presence_agents'),
			}),
			name: agent.name,
		}));
	}, [agents]);

	return (
		<AvatarGroup
			testId="agent-presence-avatar-group"
			appearance="stack"
			size="small"
			maxCount={MAX_GROUP_SIZE}
			data={data}
			shouldPopupRenderToParent={false}
			onMoreClick={onMoreClick}
			overrides={{
				Avatar: {
					render: renderAgent(agents, data.length),
				},
				MoreIndicator: {
					render: renderMoreIndicator(isOpen, data.length - MAX_GROUP_SIZE + 1),
				},
			}}
		/>
	);
};
